import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
} from '@backstage/theme';

const palettes = {
  dark: {
    type: 'dark' as const,
    mode: 'dark' as const,
    background: {
      default: '#2f3645',
      paper: '#424B5F',
    },
    status: {
      ok: '#71CF88',
      warning: '#FFB84D',
      error: '#F84C55',
      running: '#3488E3',
      pending: '#FEF071',
      aborted: '#9E9E9E',
    },
    bursts: {
      fontColor: '#FEFEFE',
      slackChannelText: '#ddd',
      backgroundColor: {
        default: '#7C3699',
      },
      gradient: {
        linear: 'linear-gradient(-137deg, #4BB8A5 0%, #187656 100%)',
      },
    },
    primary: {
      main: '#50B9FF',
      contrastText: '#fff',
      dark: '#1C85D3',
    },
    secondary: {
      main: '#FF88B2',
    },
    common: {
      white: '#FFFFFF',
      black: '#000000',
    },
    grey: {
      600: '#60687A',
      700: '#51596C',
      800: '#2F3645',
    },
    text: {
      primary: '#D4D6DC',
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
    },
    banner: {
      info: '#2E77D0',
      error: '#E22134',
      text: '#FFFFFF',
      link: '#000000',
      closeButtonColor: '#FFFFFF',
      warning: '#FF9800',
    },
    border: '#525B6E',
    textContrast: '#FFFFFF',
    textVerySubtle: '#727272',
    textSubtle: '#CCCCCC',
    highlight: '#FFFBCC',
    errorBackground: '#FFEBEE',
    warningBackground: '#F59B23',
    infoBackground: '#ebf5ff',
    errorText: '#CA001B',
    infoText: '#004e8a',
    warningText: '#000000',
    linkHover: '#1C85D3',
    link: '#50B9FF',
    gold: '#FFD600',
    button: {
      containedPrimary: '#2C6DF8',
      containedPrimaryHover: '#3D78FA',
      containedPrimaryActive: '#1798F6',
    },
    navigation: {
      background: '#424B5F',
      indicator: '#5BC3F5',
      color: '#b5b5b5',
      selectedColor: '#FFF',
      navItem: {
        hoverBackground: '#60687A',
      },
      submenu: {
        background: '#60687A',
      },
    },
    pinSidebarButton: {
      icon: '#60687A',
      background: '#BDBDBD',
    },
    tabbar: {
      indicator: '#5BC3F5',
    },
    divider: '#525B6E',
  },
};

export const adpCustomShapes = {
  space: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='400' fill='none'%3e%3cdefs%3e%3cradialGradient id='n1' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(210 182) rotate(10) scale(336 182)'%3e%3cstop stop-color='white' stop-opacity='.13'/%3e%3cstop offset='.44' stop-color='white' stop-opacity='.08'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='n2' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(742 174) rotate(-12) scale(430 210)'%3e%3cstop stop-color='white' stop-opacity='.12'/%3e%3cstop offset='.42' stop-color='white' stop-opacity='.07'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='n3' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(1128 146) rotate(14) scale(312 188)'%3e%3cstop stop-color='white' stop-opacity='.14'/%3e%3cstop offset='.46' stop-color='white' stop-opacity='.09'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/radialGradient%3e%3cradialGradient id='n4' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(1332 210) rotate(6) scale(146 210)'%3e%3cstop stop-color='white' stop-opacity='.12'/%3e%3cstop offset='.5' stop-color='white' stop-opacity='.06'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/radialGradient%3e%3c/defs%3e%3ccircle cx='-34' cy='200' r='268' fill='white' fill-opacity='.04'/%3e%3ccircle cx='274' cy='198' r='224' fill='white' fill-opacity='.03'/%3e%3ccircle cx='1214' cy='188' r='288' fill='white' fill-opacity='.04'/%3e%3cellipse cx='210' cy='182' rx='336' ry='182' fill='url(%23n1)'/%3e%3cellipse cx='742' cy='174' rx='430' ry='210' fill='url(%23n2)'/%3e%3cellipse cx='1128' cy='146' rx='312' ry='188' fill='url(%23n3)'/%3e%3cellipse cx='1332' cy='210' rx='146' ry='210' fill='url(%23n4)'/%3e%3ccircle cx='44' cy='58' r='.9' fill='white' fill-opacity='.18'/%3e%3ccircle cx='58' cy='74' r='1.1' fill='white' fill-opacity='.28'/%3e%3ccircle cx='72' cy='92' r='.8' fill='white' fill-opacity='.16'/%3e%3ccircle cx='84' cy='118' r='1.3' fill='white' fill-opacity='.36'/%3e%3ccircle cx='96' cy='146' r='1.6' fill='white' fill-opacity='.44'/%3e%3ccircle cx='110' cy='168' r='.8' fill='white' fill-opacity='.14'/%3e%3ccircle cx='124' cy='188' r='1.1' fill='white' fill-opacity='.26'/%3e%3ccircle cx='138' cy='214' r='1.4' fill='white' fill-opacity='.34'/%3e%3ccircle cx='154' cy='238' r='.9' fill='white' fill-opacity='.18'/%3e%3ccircle cx='170' cy='266' r='1.2' fill='white' fill-opacity='.3'/%3e%3ccircle cx='188' cy='292' r='1.6' fill='white' fill-opacity='.42'/%3e%3ccircle cx='206' cy='318' r='1' fill='white' fill-opacity='.22'/%3e%3ccircle cx='224' cy='86' r='1.2' fill='white' fill-opacity='.24'/%3e%3ccircle cx='240' cy='116' r='.9' fill='white' fill-opacity='.14'/%3e%3ccircle cx='258' cy='142' r='1.5' fill='white' fill-opacity='.38'/%3e%3ccircle cx='276' cy='168' r='1.1' fill='white' fill-opacity='.24'/%3e%3ccircle cx='294' cy='196' r='1.8' fill='white' fill-opacity='.5'/%3e%3ccircle cx='314' cy='226' r='.8' fill='white' fill-opacity='.16'/%3e%3ccircle cx='334' cy='252' r='1.3' fill='white' fill-opacity='.34'/%3e%3ccircle cx='354' cy='276' r='1' fill='white' fill-opacity='.2'/%3e%3ccircle cx='376' cy='302' r='1.6' fill='white' fill-opacity='.46'/%3e%3ccircle cx='398' cy='330' r='1.1' fill='white' fill-opacity='.24'/%3e%3ccircle cx='434' cy='60' r='.9' fill='white' fill-opacity='.16'/%3e%3ccircle cx='462' cy='84' r='1.4' fill='white' fill-opacity='.36'/%3e%3ccircle cx='490' cy='108' r='1' fill='white' fill-opacity='.22'/%3e%3ccircle cx='520' cy='132' r='1.8' fill='white' fill-opacity='.5'/%3e%3ccircle cx='548' cy='156' r='1.1' fill='white' fill-opacity='.24'/%3e%3ccircle cx='576' cy='182' r='.8' fill='white' fill-opacity='.14'/%3e%3ccircle cx='604' cy='208' r='1.3' fill='white' fill-opacity='.3'/%3e%3ccircle cx='632' cy='234' r='1' fill='white' fill-opacity='.22'/%3e%3ccircle cx='660' cy='262' r='1.7' fill='white' fill-opacity='.48'/%3e%3ccircle cx='688' cy='292' r='.9' fill='white' fill-opacity='.16'/%3e%3ccircle cx='716' cy='322' r='1.2' fill='white' fill-opacity='.28'/%3e%3ccircle cx='746' cy='348' r='1.5' fill='white' fill-opacity='.36'/%3e%3ccircle cx='780' cy='54' r='1.1' fill='white' fill-opacity='.24'/%3e%3ccircle cx='808' cy='80' r='.8' fill='white' fill-opacity='.12'/%3e%3ccircle cx='838' cy='104' r='1.6' fill='white' fill-opacity='.42'/%3e%3ccircle cx='868' cy='128' r='1' fill='white' fill-opacity='.2'/%3e%3ccircle cx='898' cy='152' r='1.4' fill='white' fill-opacity='.34'/%3e%3ccircle cx='928' cy='178' r='.9' fill='white' fill-opacity='.16'/%3e%3ccircle cx='958' cy='204' r='1.8' fill='white' fill-opacity='.52'/%3e%3ccircle cx='988' cy='228' r='1.2' fill='white' fill-opacity='.26'/%3e%3ccircle cx='1018' cy='252' r='.8' fill='white' fill-opacity='.14'/%3e%3ccircle cx='1048' cy='278' r='1.5' fill='white' fill-opacity='.4'/%3e%3ccircle cx='1078' cy='304' r='1' fill='white' fill-opacity='.2'/%3e%3ccircle cx='1108' cy='332' r='1.7' fill='white' fill-opacity='.46'/%3e%3ccircle cx='1142' cy='72' r='.9' fill='white' fill-opacity='.16'/%3e%3ccircle cx='1174' cy='96' r='1.3' fill='white' fill-opacity='.3'/%3e%3ccircle cx='1206' cy='120' r='1' fill='white' fill-opacity='.22'/%3e%3ccircle cx='1238' cy='146' r='1.9' fill='white' fill-opacity='.54'/%3e%3ccircle cx='1270' cy='172' r='1.2' fill='white' fill-opacity='.26'/%3e%3ccircle cx='1302' cy='198' r='.8' fill='white' fill-opacity='.14'/%3e%3ccircle cx='1332' cy='226' r='1.4' fill='white' fill-opacity='.34'/%3e%3ccircle cx='1360' cy='252' r='1' fill='white' fill-opacity='.18'/%3e%3ccircle cx='116' cy='44' r='2.4' fill='white' fill-opacity='.68'/%3e%3ccircle cx='286' cy='78' r='2.1' fill='white' fill-opacity='.62'/%3e%3ccircle cx='522' cy='84' r='2.6' fill='white' fill-opacity='.74'/%3e%3ccircle cx='696' cy='134' r='2.2' fill='white' fill-opacity='.66'/%3e%3ccircle cx='958' cy='76' r='2.8' fill='white' fill-opacity='.78'/%3e%3ccircle cx='1188' cy='106' r='2.3' fill='white' fill-opacity='.64'/%3e%3ccircle cx='1288' cy='154' r='2.5' fill='white' fill-opacity='.7'/%3e%3ccircle cx='242' cy='230' r='2.2' fill='white' fill-opacity='.62'/%3e%3ccircle cx='474' cy='214' r='2.6' fill='white' fill-opacity='.72'/%3e%3ccircle cx='842' cy='214' r='2.4' fill='white' fill-opacity='.68'/%3e%3ccircle cx='1086' cy='236' r='2.8' fill='white' fill-opacity='.76'/%3e%3ccircle cx='1242' cy='286' r='2.3' fill='white' fill-opacity='.64'/%3e%3ccircle cx='348' cy='338' r='2.1' fill='white' fill-opacity='.58'/%3e%3ccircle cx='914' cy='332' r='2.5' fill='white' fill-opacity='.7'/%3e%3ccircle cx='1186' cy='340' r='2.2' fill='white' fill-opacity='.62'/%3e%3ccircle cx='24' cy='36' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='36' cy='48' r='.6' fill='white' fill-opacity='.1'/%3e%3ccircle cx='52' cy='60' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='66' cy='72' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='82' cy='84' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='98' cy='98' r='.6' fill='white' fill-opacity='.1'/%3e%3ccircle cx='114' cy='112' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='132' cy='126' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='150' cy='142' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='168' cy='156' r='.6' fill='white' fill-opacity='.1'/%3e%3ccircle cx='186' cy='170' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='204' cy='186' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='222' cy='202' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='242' cy='218' r='.6' fill='white' fill-opacity='.1'/%3e%3ccircle cx='262' cy='234' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='282' cy='250' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='304' cy='266' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='326' cy='282' r='.6' fill='white' fill-opacity='.1'/%3e%3ccircle cx='348' cy='298' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='372' cy='314' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='396' cy='330' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='420' cy='346' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='456' cy='40' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='480' cy='56' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='506' cy='72' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='532' cy='88' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='558' cy='104' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='584' cy='122' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='610' cy='140' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='638' cy='158' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='666' cy='176' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='694' cy='194' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='722' cy='212' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='750' cy='230' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='778' cy='248' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='806' cy='266' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='834' cy='284' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='862' cy='302' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='890' cy='320' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='918' cy='338' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='962' cy='42' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='990' cy='58' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='1018' cy='76' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='1046' cy='94' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='1074' cy='112' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='1102' cy='130' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='1130' cy='148' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='1158' cy='166' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='1186' cy='184' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='1214' cy='202' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='1242' cy='220' r='.7' fill='white' fill-opacity='.12'/%3e%3ccircle cx='1270' cy='238' r='.6' fill='white' fill-opacity='.08'/%3e%3ccircle cx='1298' cy='256' r='.7' fill='white' fill-opacity='.11'/%3e%3ccircle cx='1326' cy='274' r='.6' fill='white' fill-opacity='.09'/%3e%3ccircle cx='1354' cy='292' r='.7' fill='white' fill-opacity='.12'/%3e%3c/svg%3e")`,
};

const adpPageDarkTheme = {
  home: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  documentation: genPageTheme({
    colors: ['#82238E', '#A81865'],
    shape: adpCustomShapes.space,
  }),
  tool: genPageTheme({
    colors: ['#3C288C', '#673B84'],
    shape: adpCustomShapes.space,
  }),
  service: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  website: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  library: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  other: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  app: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
  apis: genPageTheme({
    colors: ['#0A6C5F', '#1889A8'],
    shape: adpCustomShapes.space,
  }),
  card: genPageTheme({
    colors: ['#294984', '#0B6769'],
    shape: adpCustomShapes.space,
  }),
};

export const adpDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: palettes.dark,
  }),
  typography: {
    htmlFontSize: 16,
    fontFamily: "'PT Astra Fact', 'Roboto', sans-serif",
    h1: {
      fontSize: 20,
      fontWeight: 600,
      marginBottom: 24,
    },
    h2: {
      fontSize: 20,
      fontWeight: 600,
      marginBottom: 16,
    },
    h3: {
      fontSize: 18,
      fontWeight: 600,
      marginBottom: 24,
    },
    h4: {
      fontWeight: 600,
      fontSize: 16,
      marginBottom: 24,
    },
    h5: {
      fontWeight: 600,
      fontSize: 14,
      marginBottom: 24,
    },
    h6: {
      fontWeight: 600,
      fontSize: 14,
      marginBottom: 16,
    },
  },
  pageTheme: adpPageDarkTheme,
  components: {
    BackstageHeader: {
      styleOverrides: {
        header: {
          minHeight: 100,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          textTransform: 'none',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        secondaryAction: {
          '&:empty': {
            width: 0,
            marginRight: 0,
          },
        },
      },
    },
    BackstageSelectInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '& .MuiSvgIcon-root': {
            color: theme.palette.text.primary,
          },
          '&:hover .MuiSvgIcon-root': {
            color: theme.palette.common.white,
          },
        }),
        input: ({ theme }) => ({
          backgroundColor: theme.palette.grey[800],
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          padding: '8px 26px 8px 12px',
          '&:hover': {
            backgroundColor: theme.palette.grey[700],
            borderColor: theme.palette.divider,
            color: theme.palette.common.white,
          },
          '&:focus': {
            backgroundColor: theme.palette.grey[800],
            borderRadius: theme.shape.borderRadius,
          },
        }),
      },
    },
    BackstageClosedDropdown: {
      styleOverrides: {
        icon: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    BackstageOpenedDropdown: {
      styleOverrides: {
        icon: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: ({ theme }) => ({
          borderTop: `1px solid ${theme.palette.border}`,
          borderBottom: `1px solid ${theme.palette.border}`,
        }),
      },
    },
    PluginCatalogEntityLinksEmptyState: {
      styleOverrides: {
        code: {
          background: 'var(--bui-bg-app)',
        },
      },
    },
    CatalogReactUserListPicker: {
      styleOverrides: {
        title: {
          textTransform: 'none',
        },
        menuItem: {
          minHeight: 40,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: ({ theme }) => ({
          '&[class*="MuiOutlinedInput-root"]': {
            padding: '2px 8px',
            backgroundColor: theme.palette.grey[800],
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '& .MuiAutocomplete-input, & .MuiInputBase-input': {
              color: theme.palette.text.primary,
            },
            '& .MuiSvgIcon-root': {
              color: theme.palette.text.primary,
            },
            '&:hover': {
              backgroundColor: theme.palette.grey[700],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiAutocomplete-input, &:hover .MuiInputBase-input, &:hover .MuiSvgIcon-root':
              {
                color: theme.palette.common.white,
              },
          },
        }),
        popupIndicator: ({ theme }) => ({
          color: `${theme.palette.text.primary} !important`,
          '&:hover': {
            color: `${theme.palette.common.white} !important`,
          },
        }),
        clearIndicator: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            color: theme.palette.common.white,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[800],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '& .MuiSelect-select': {
            color: theme.palette.text.primary,
          },
          '& .MuiSelect-icon': {
            color: theme.palette.text.primary,
          },
          '&:hover': {
            backgroundColor: theme.palette.grey[700],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },
          '&:hover .MuiSelect-select, &:hover .MuiSelect-icon': {
            color: theme.palette.common.white,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        outlined: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.divider,
          },
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }),
        outlinedPrimary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.divider,
          },
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
        }),
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.button.containedPrimary,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.button.containedPrimaryHover,
            color: theme.palette.common.white,
          },
          '&:active': {
            backgroundColor: theme.palette.button.containedPrimaryActive,
            color: theme.palette.common.white,
          },
          '&.Mui-disabled': {
            opacity: 0.32,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: '11.5px 14px',
        },
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.border}`,
        }),
        head: ({ theme }) => ({
          borderTop: `1px solid ${theme.palette.border}`,
          borderBottom: `1px solid ${theme.palette.border}`,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.grey[600],
          },
        }),
      },
    },
    // MuiLink: {
    //   styleOverrides: {
    //     root: ({ theme }) => ({
    //       color: theme.palette.link,
    //       '&:hover': {
    //         color: theme.palette.linkHover,
    //       },
    //     }),
    //   },
    // },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
        sticky: {
          position: 'static',
          top: 'auto',
          zIndex: 'auto',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        button: {
          textTransform: 'none',
        },
      },
    },
  },
});
